import { fg } from '@atlaskit/platform-feature-flags';

import {
	DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
	DEFAULT_CLOUD_GRAPH_VIDEO_ENTITY_ATI,
} from '../../entities';
import type { FilterId } from '../../filters';
import {
	NounSubtypeKey,
	type TypeFilterValue3PEntities,
	TypeFilterValueKey,
} from '../../filters/universal-type';
import { get3PLogo } from '../../icons';
import { type ThirdPartyConfig } from '../../schemas/3p-config';

import { SharePointIcon } from './icon';
import { SharepointIconBorderless } from './icon-borderless';
import { SharePointSearchResultIcon } from './search-result-icon';

const getSharePointTypeFilterConfig = () => {
	const config = new Map<TypeFilterValueKey, TypeFilterValue3PEntities>();

	config.set(TypeFilterValueKey.Document, {
		entities3P: DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
		subTypes3P: fg('rovo_fps_enable_sharepoint_new_types_filter')
			? [NounSubtypeKey.Document, NounSubtypeKey.Pdf, NounSubtypeKey.Page, NounSubtypeKey.Blogpost]
			: [NounSubtypeKey.Document, NounSubtypeKey.Pdf],
	});

	config.set(TypeFilterValueKey.Spreadsheet, {
		entities3P: DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
		subTypes3P: [NounSubtypeKey.Spreadsheet],
	});

	config.set(TypeFilterValueKey.Presentation, {
		entities3P: DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
		subTypes3P: [NounSubtypeKey.Presentation],
	});

	if (fg('rovo_fps_sharepoint_enable_video_noun_filter')) {
		config.set(TypeFilterValueKey.Video, {
			entities3P: DEFAULT_CLOUD_GRAPH_VIDEO_ENTITY_ATI,
			subTypes3P: [NounSubtypeKey.Video],
		});
	}

	if (fg('rovo_fps_enable_sharepoint_new_types_filter')) {
		config.set(TypeFilterValueKey.Image, {
			entities3P: DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
			subTypes3P: [NounSubtypeKey.Image],
		});
		config.set(TypeFilterValueKey.Folder, {
			entities3P: DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
			subTypes3P: [NounSubtypeKey.Folder, NounSubtypeKey.Drive],
		});
		// Document -> video fallback (only when video noun filter is OFF)
		if (!fg('rovo_fps_sharepoint_enable_video_noun_filter')) {
			config.set(TypeFilterValueKey.Video, {
				entities3P: DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
				subTypes3P: [NounSubtypeKey.Video],
			});
		}
		config.set(TypeFilterValueKey.Pdf, {
			entities3P: DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
			subTypes3P: [NounSubtypeKey.Pdf],
		});
		config.set(TypeFilterValueKey.Notebook, {
			entities3P: DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
			subTypes3P: [NounSubtypeKey.Notebook],
		});

		config.set(TypeFilterValueKey.WebPage, {
			entities3P: DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
			subTypes3P: [NounSubtypeKey.WebPage, NounSubtypeKey.Shortcut],
		});
		config.set(TypeFilterValueKey.Other, {
			entities3P: DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
			subTypes3P: [NounSubtypeKey.Other, NounSubtypeKey.Archive],
		});
	}

	// Comments are embedded within documents as linkedEntities, not standalone entities.
	// Keep the entity type aligned to document results so any consumer of typeFilterConfig still
	// treats SharePoint comment matches as documents rather than standalone comments.
	// SharePoint embedded comments are gated behind a SharePoint-specific flag (independent of the
	// shared 3p flag used for Google Drive) so we can enable/disable SharePoint comment support
	// independently of other 3p products.
	if (fg('rovo_fps_sharepoint_embedded_comments')) {
		config.set(TypeFilterValueKey.Comment, {
			entities3P: DEFAULT_CLOUD_GRAPH_DOCUMENT_ENTITY_ATI,
			subTypes3P: [],
		});
	}

	return config;
};

export const sharepointConfig: ThirdPartyConfig = {
	displayName: 'Microsoft SharePoint',
	id: 'sharepoint',
	resourceType: 'sharepoint-connector',
	icon: get3PLogo(SharePointIcon, 'Sharepoint', 'medium'),
	iconBorderless: get3PLogo(SharepointIconBorderless, 'Sharepoint', 'small'),
	searchResultIcon: get3PLogo(SharePointSearchResultIcon, 'Sharepoint', 'large'),
	integrationAri: 'ari:cloud:platform::integration/microsoft',
	// Use a getter to evaluate lazily so that feature flags are evaluated when accessed, not at module load.
	get typeFilterConfig() {
		return getSharePointTypeFilterConfig();
	},
	get filters(): FilterId[] {
		const baseFilters: FilterId[] = ['lastUpdated', 'onlyTitleMatch'];
		if (fg('rovo_fps_enable_sharepoint_space_filter')) {
			baseFilters.push('space');
		}
		if (fg('rovo_fps_enable_sharepoint_content_filter')) {
			baseFilters.push('content');
		}
		return baseFilters;
	},
	requiresOauth: true,
	isEnabled: () => true,
};
